import { useMemo } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { Toaster } from 'sonner'
import { createQueryClient } from '@/lib/query'
import { routeTree } from './routeTree.gen'

/**
 * App root.
 *
 * Builds one QueryClient and one Router, both kept stable across
 * renders. The router gets the queryClient via `context`, so child
 * routes can use `context.queryClient.ensureQueryData(...)` in loaders.
 */
export function App() {
  const queryClient = useMemo(() => createQueryClient(), [])
  const router = useMemo(
    () =>
      createRouter({
        routeTree,
        context: { queryClient },
        defaultPreload: 'intent',
        defaultPreloadStaleTime: 0,
        // Partner-API pages (matches, dashboard) take seconds to load; without
        // this every route rendered a blank screen until its loader resolved,
        // which reads as a hung page. Routes can still override it (dashboard
        // ships its own DashboardPending).
        defaultPendingComponent: RoutePending,
      }),
    [queryClient],
  )

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster
        position="top-right"
        richColors
        closeButton
        duration={5000}
        toastOptions={{
          className: 'font-sans',
        }}
      />
    </QueryClientProvider>
  )
}

function RoutePending() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-muted flex items-center gap-2 text-sm font-medium">
        <Loader2 className="size-4 animate-spin" />
        Loading...
      </div>
    </div>
  )
}

/**
 * Register the router type so `useNavigate`, `useParams`, `<Link to>` and
 * friends are fully typed across the app.
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>
  }
}
